/* eslint-disable @typescript-eslint/no-explicit-any */
import { Transition } from "@headlessui/react";
import classNames from "classnames";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import filterIcon from "src/assets/filterIcon.svg";
import { Button, Input } from "src/components";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select.component";
import IavQueueFiltersComponent from "src/container/invoice-ack/iav-queue-filters.component";
import { IavFilters, iavActions } from "src/context/iav-slice";
import { RootState } from "src/context/store";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { getAllCompanyDetails } from "src/services/company-api";
import { queryDivisionDetails } from "src/services/division-api";
import { getOpsQueue, getRevokeReasons } from "src/services/iav-api";
import { getAssigneeList } from "src/services/invoice-api";
import {
  IAV_CATEGORY,
  IAV_CATEGORY_LABELS,
  IAV_SEARCH_OPTIONS,
  formatIavDate,
  iavDetailUrl,
  iavQueueAgeDays,
  iavStatusLabel,
} from "./iav-helpers";

const PAGE_SIZE = 20;
const CATEGORIES = [IAV_CATEGORY.STAMP_SIGN_REVIEW, IAV_CATEGORY.DISPUTE];

const joinKeys = (items: any[], key: string) => items?.map((item) => item?.[key]).filter((v) => v != null).join(",") || undefined;

// Same param names as the Invoice Entry / Approval list API, so the backend filters read alike
const buildQueueParams = (filters: IavFilters, page: number) => ({
  category: filters.category,
  page,
  limit: PAGE_SIZE,
  company_code: joinKeys(filters.company, "code"),
  division_code: joinKeys(filters.division, "company_divisioncode"),
  assignee_id: filters.category === IAV_CATEGORY.DISPUTE ? undefined : joinKeys(filters.assignee, "invoice_assignment_id"),
  status: joinKeys(filters.status, "key"),
  ordering: filters.sorting?.[0]?.key,
  ...(filters.dateType?.[0]?.key && filters.dateRange?.from
    ? { dateType: filters.dateType[0].key, startDate: filters.dateRange.from, endDate: filters.dateRange.to }
    : {}),
  ...(filters.searchValue ? { [filters.searchCategory]: filters.searchValue } : {}),
});

// Number of active filters, shown on the filter button like the Entry tab
const activeFilterCount = (filters: IavFilters) =>
  [filters.company, filters.division, filters.assignee, filters.status, filters.sorting, filters.dateType].filter(
    (value) => value?.length > 0,
  ).length;

const InvoiceAckQueue = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { queueList, queueTotal, queuePage, filters, filterOptions, isLoading } = useSelector(
    (state: RootState) => state.iav,
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useOutsideClick(() => setIsFilterOpen(false));

  const isDispute = filters.category === IAV_CATEGORY.DISPUTE;
  const companyCode = filters.company?.[0]?.code;
  // Re-fetch whenever any filter changes; paging reuses the same filters
  const filterKey = useMemo(() => JSON.stringify(buildQueueParams(filters, 1)), [filters]);

  const fetchQueue = async (page = 1) => {
    dispatch(iavActions.setLoading(true));
    try {
      const res = await getOpsQueue(buildQueueParams(filters, page));
      if (res.status === 200) {
        // OMS wraps every result as { name, success, data } — the queue is under data
        const queue = res.data?.data ?? {};
        dispatch(iavActions.setQueueList({ items: queue.items ?? [], total: queue.total ?? 0 }));
        dispatch(iavActions.setQueuePage(page));
      }
    } catch (err) {
      console.error("IAV queue fetch error:", err);
    } finally {
      dispatch(iavActions.setLoading(false));
    }
  };

  useEffect(() => {
    fetchQueue(1);
  }, [filterKey]);

  // Filter options and revoke reasons load once, like the Entry tab
  useEffect(() => {
    getRevokeReasons()
      .then((res) => dispatch(iavActions.setRevokeReasons(Array.isArray(res.data?.data) ? res.data.data : [])))
      .catch(console.error);
    if (!filterOptions.company.length) {
      getAllCompanyDetails()
        .then((res) => dispatch(iavActions.setFilterOptions({ company: res?.data ?? [] })))
        .catch(console.error);
    }
  }, []);

  // Divisions and assignees depend on the first selected company (same as the Entry tab)
  useEffect(() => {
    if (companyCode) {
      queryDivisionDetails(`/details/?fields=division_name&company_code=${companyCode}`)
        .then((res: any) => dispatch(iavActions.setFilterOptions({ division: res?.data ?? [] })))
        .catch(console.error);
    } else {
      dispatch(iavActions.setFilterOptions({ division: [] }));
    }
    getAssigneeList(companyCode)
      .then((res) => dispatch(iavActions.setFilterOptions({ assignee: res?.data?.data ?? [] })))
      .catch(console.error);
  }, [companyCode]);

  const selectCategory = (category: string) => {
    if (category === filters.category) return;
    // Status values differ per category, so they don't carry over
    dispatch(iavActions.setFilters({ category, status: [] }));
  };

  const openInvoice = (invoice: any) => {
    dispatch(iavActions.setSelectedInvoice(invoice));
    navigate(iavDetailUrl(filters.category, invoice.invoice_id));
  };

  const headers = [
    "Invoice No.",
    "Invoice Date",
    "Counter",
    "Stockist",
    "Company",
    "Status",
    ...(isDispute ? ["Dispute Started"] : ["Assigned To", "Assigned At"]),
    "Age (days)",
  ];

  return (
    <div className="w-full">
      <Transition
        as={Fragment}
        show={isFilterOpen}
        enter="transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed right-0 inset-0 top-0 z-[500]">
          <IavQueueFiltersComponent ref={filterRef} onClose={() => setIsFilterOpen(false)} />
        </div>
      </Transition>

      <div className="px-2 py-2 w-full sm:px-0">
        <div className="flex justify-between items-center gap-4 mb-4">
          {/* Category sub-tabs — same style as the Entry / Approval sub-tabs */}
          <div className="flex p-1 space-x-1 rounded-lg bg-[#e3e3e3] w-[440px] h-[38px] items-center">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => selectCategory(category)}
                className={classNames(
                  "w-full rounded-md py-1.5 text-sm font-normal leading-5 focus:outline-none",
                  filters.category === category ? "bg-white" : "hover:bg-white/[0.12] hover:text-black",
                )}
              >
                {IAV_CATEGORY_LABELS[category]}
                {filters.category === category && (
                  <span className="ml-4 bg-[#8051FF] text-white text-[12px] px-2 py-1 rounded-lg">{queueTotal}</span>
                )}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 h-full">
            <div className="flex items-center w-[400px] rounded-md overflow-hidden border border-gray-300 bg-white">
              <Select
                value={filters.searchCategory}
                onValueChange={(value) => dispatch(iavActions.setFilters({ searchCategory: value }))}
              >
                <SelectTrigger className="w-[180px] rounded-none border-none bg-white text-gray-700 focus:ring-0 focus:outline-none focus:shadow-none">
                  <SelectValue placeholder="Search By" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {IAV_SEARCH_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Search"
                debounceTime={1250}
                value={filters.searchValue}
                onChange={(e) => dispatch(iavActions.setFilters({ searchValue: e.target.value.trim() }))}
                containerClasses="w-full px-3 py-1 text-gray-700 border-none bg-white focus:outline-none focus:ring-0 focus:shadow-none"
              />
            </div>
            <div className="flex items-center gap-2 h-full relative">
              {activeFilterCount(filters) > 0 && (
                <span className="absolute top-[-10px] right-[-10px] w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">
                  {activeFilterCount(filters)}
                </span>
              )}
              <Button className="bg-[#F1F1F1]" onClick={() => setIsFilterOpen(true)}>
                <img src={filterIcon} alt="filter-icon" />
              </Button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12 text-gray-500">Loading...</div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {headers.map((header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {queueList.length === 0 ? (
                    <tr>
                      <td colSpan={headers.length} className="px-4 py-8 text-center text-gray-400">
                        No items in queue
                      </td>
                    </tr>
                  ) : (
                    queueList.map((item) => {
                      const sia = item.stockistInvoiceAttachment ?? {};
                      const age = iavQueueAgeDays(item, filters.category);
                      return (
                        <tr
                          key={item.invoice_id}
                          onClick={() => openInvoice(item)}
                          className="cursor-pointer hover:bg-blue-50 transition-colors text-sm"
                        >
                          <td className="px-4 py-3 text-gray-900 font-medium">{item.invoice_number}</td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatIavDate(item.new_invoice_date)}</td>
                          <td className="px-4 py-3">
                            <div className="text-gray-900">{sia.counter_code || sia.pharmacy_code || "—"}</div>
                            <div className="text-xs text-gray-500">{sia.counter_name || sia.pharmacy_name || ""}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-gray-900">{sia.stockist_name || "—"}</div>
                            <div className="text-xs text-gray-500">{sia.company_stockist_code || ""}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{sia.company_name || sia.company_code || "—"}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs rounded-full bg-[#ede9fe] text-[#5b21b6] whitespace-nowrap">
                              {iavStatusLabel(item.invoice_ack_status_id)}
                            </span>
                          </td>
                          {isDispute ? (
                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatIavDate(item.dispute_email_sent_at)}</td>
                          ) : (
                            <>
                              <td className="px-4 py-3 text-gray-600">{item.ack_assigned_to_user_name || "—"}</td>
                              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatIavDate(item.ack_assigned_at)}</td>
                            </>
                          )}
                          <td className="px-4 py-3 text-gray-600">{age ?? "—"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {queueTotal > PAGE_SIZE && (
              <div className="flex justify-between items-center mt-4">
                <span className="text-sm text-gray-500">Total: {queueTotal}</span>
                <div className="flex gap-2">
                  <button
                    disabled={queuePage <= 1}
                    onClick={() => fetchQueue(queuePage - 1)}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-50"
                  >
                    Prev
                  </button>
                  <span className="px-3 py-1 text-sm">{queuePage}</span>
                  <button
                    disabled={queuePage * PAGE_SIZE >= queueTotal}
                    onClick={() => fetchQueue(queuePage + 1)}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InvoiceAckQueue;
